import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Buildings, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * TransferDialog — Super-Admin-only branch transfer helper.
 * Props:
 *   open, onOpenChange (bool + setter)
 *   entity: "customer" | "application" | "pos_bill" | "rent_agreement" | "complaint"
 *   entityId: string mongo id
 *   entityLabel: string (e.g. customer name or app_no) shown in the header
 *   fromBranchId: current branch_id (optional, hidden from destination list)
 *   onTransferred: callback after successful move
 */
export default function TransferDialog({ open, onOpenChange, entity, entityId, entityLabel, fromBranchId, onTransferred }) {
  const [branches, setBranches] = useState([]);
  const [toBranch, setToBranch] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Explicitly bypass the branch_id interceptor injection by passing empty
    api.get("/branches", { params: { branch_id: "" } })
      .then((r) => setBranches(r.data || []))
      .catch(() => setBranches([]));
    setToBranch(""); setReason("");
  }, [open]);

  const submit = async () => {
    if (!toBranch) { toast.error("Pick a destination branch"); return; }
    setBusy(true);
    try {
      await api.post("/transfer", {
        entity, entity_id: entityId, to_branch_id: toBranch, reason,
      });
      toast.success("Transferred");
      onTransferred?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Transfer failed");
    } finally {
      setBusy(false);
    }
  };

  const destinations = branches.filter((b) => b.id !== fromBranchId);
  const from = branches.find((b) => b.id === fromBranchId);
  const to   = branches.find((b) => b.id === toBranch);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Buildings size={18} weight="duotone" className="text-orange-500" />
            Transfer {entity.replace("_", " ")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {entityLabel && <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-800 font-medium" data-testid="transfer-entity-label">{entityLabel}</div>}

          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-xs">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">From</div>
              <div className="rounded-md bg-slate-100 border border-slate-200 p-2 font-semibold text-slate-800">{from ? `${from.code} · ${from.name}` : "—"}</div>
            </div>
            <ArrowRight size={16} className="text-slate-400 mt-4" />
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">To</div>
              <div className={`rounded-md p-2 font-semibold ${to ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-slate-100 border border-dashed border-slate-300 text-slate-400"}`}>{to ? `${to.code} · ${to.name}` : "Pick branch…"}</div>
            </div>
          </div>

          <div>
            <Label>Destination branch *</Label>
            <Select value={toBranch} onValueChange={setToBranch}>
              <SelectTrigger data-testid="transfer-to-branch"><SelectValue placeholder="Choose branch…" /></SelectTrigger>
              <SelectContent>
                {destinations.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}{b.city ? ` (${b.city})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Reason (optional but recommended)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer relocated · Operator moved · Data correction" data-testid="transfer-reason" />
          </div>

          <div className="text-[10px] text-slate-500 italic">🔒 Super Admin only · Every transfer is written to the audit log with timestamp, operator and reason.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !toBranch} className="bg-orange-600 hover:bg-orange-700 text-white gap-2" data-testid="transfer-confirm">
            {busy ? "Transferring…" : <>Transfer <ArrowRight size={13} weight="bold" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
